/**
 * themeSchema — the declarative field list that drives the builder's global Theme-settings editor
 * (feature 0019 Slice 2). Each entry maps ONE `ThemeSettings` leaf to a reusable inspector control, with
 * a typed `get` (read the value) + `patch` (produce a `ThemeSettingsPatch` for `updateThemeSettings`).
 *
 * The panel renders these groups through the existing FieldRow + Color/Range/Select/Gradient controls, so
 * the editor is data-driven (no hand-built form) and stays in lockstep with the model.
 *
 * FONTS: every option's `value` is a full CSS font stack whose PRIMARY family (the part before the first
 * comma) is on the backend `FONT_ALLOWLIST` (StoreThemeSettings.kt) — the server rejects anything else, so
 * these must match. The Poppins stack is byte-identical to `DEFAULT_THEME_SETTINGS` so the seed shows as
 * "Poppins" selected.
 */
import type {
  CheckboxField,
  GradientField,
  ImageField,
  ImageValue,
  ProductBadgePosition,
  RangeField,
  SelectField,
  TextField,
  ThemeSettings,
} from '@aquilixshop/ui-react';
import { DEFAULT_PRODUCT_BADGES } from '@aquilixshop/ui-react';
import type { ThemeSettingsPatch } from '../state/builderStore';

/** A curated font stack whose primary family is allowlisted server-side (SelectControl `value`). */
interface FontChoice {
  label: string;
  stack: string;
}

const SANS_TAIL = "system-ui, -apple-system, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif";

/** The curated fonts the editor offers — order + primary families MATCH the backend allowlist (D5). */
export const THEME_FONTS: FontChoice[] = [
  // Byte-identical to DEFAULT_THEME_SETTINGS' POPPINS_STACK so the seeded theme shows Poppins selected.
  { label: 'Poppins', stack: `'Poppins', ${SANS_TAIL}` },
  { label: 'Inter', stack: `'Inter', ${SANS_TAIL}` },
  { label: 'Roboto', stack: `'Roboto', ${SANS_TAIL}` },
  { label: 'Montserrat', stack: `'Montserrat', ${SANS_TAIL}` },
  { label: 'Open Sans', stack: `'Open Sans', ${SANS_TAIL}` },
  { label: 'Lato', stack: `'Lato', ${SANS_TAIL}` },
  { label: 'Nunito', stack: `'Nunito', ${SANS_TAIL}` },
  { label: 'Work Sans', stack: `'Work Sans', ${SANS_TAIL}` },
  { label: 'Playfair Display', stack: `'Playfair Display', Georgia, 'Times New Roman', serif` },
  // La seule serif du produit qui soit RÉELLEMENT servie : Fraunces est auto-hébergée dans
  // `@aquilixshop/tokens` (latin + latin-ext, variable 400-600). Playfair juste au-dessus ne l'est pas
  // et retombe sur Georgia — c'est le défaut connu de Bloom, laissé tel quel ici parce que le corriger
  // veut dire embarquer une famille de plus, pas retoucher cette liste.
  { label: 'Fraunces', stack: `'Fraunces', Georgia, 'Times New Roman', serif` },
  // Auto-hébergée elle aussi (latin + latin-ext, variable 400-700) : la slab du thème café Roast.
  { label: 'Bitter', stack: `'Bitter', Georgia, 'Times New Roman', serif` },
  // Auto-hébergée (latin + latin-ext, variable 400-700) : la garalde du thème librairie Folio.
  { label: 'EB Garamond', stack: `'EB Garamond', Georgia, 'Times New Roman', serif` },
  { label: 'DM Sans', stack: `'DM Sans', ${SANS_TAIL}` },
  { label: 'System UI', stack: SANS_TAIL },
];

const FONT_OPTIONS = THEME_FONTS.map((f) => ({ value: f.stack, label: f.label }));

// ── entry kinds (discriminated by `kind`; each carries its own control-field + typed get/patch) ─────

export interface ThemeColorEntry {
  kind: 'color';
  field: { id: string; label: string; info?: string };
  get: (t: ThemeSettings) => string;
  patch: (hex: string) => ThemeSettingsPatch;
  /** `true` for the solid-button-label role → the panel shows the WCAG contrast warning under it (#16). */
  contrastAgainstAccent?: boolean;
}
export interface ThemeGradientEntry {
  kind: 'gradient';
  field: GradientField;
  get: (t: ThemeSettings) => string;
  patch: (css: string) => ThemeSettingsPatch;
}
export interface ThemeRangeEntry {
  kind: 'range';
  field: RangeField;
  get: (t: ThemeSettings) => number;
  patch: (n: number) => ThemeSettingsPatch;
}
export interface ThemeSelectEntry {
  kind: 'select';
  field: SelectField;
  get: (t: ThemeSettings) => string;
  /** `t` = the theme as it is BEFORE the change, so an entry can patch a sibling field in lockstep. */
  patch: (s: string, t: ThemeSettings) => ThemeSettingsPatch;
  /**
   * Translation-key prefix for the OPTION labels: `<prefix>.<label slugified>`. The font selects use
   * `chrome.theme.font` (a shared list reused by both of them, and mostly proper nouns that never
   * translate); any other select keys off its own field id, so two selects cannot collide.
   */
  optionKeyPrefix: string;
}
export interface ThemeImageEntry {
  kind: 'image';
  field: ImageField;
  get: (t: ThemeSettings) => ImageValue | undefined;
  patch: (v: ImageValue | undefined) => ThemeSettingsPatch;
}
export interface ThemeTextEntry {
  kind: 'text';
  field: TextField;
  get: (t: ThemeSettings) => string;
  patch: (s: string) => ThemeSettingsPatch;
}
export interface ThemeCheckboxEntry {
  kind: 'checkbox';
  field: CheckboxField;
  get: (t: ThemeSettings) => boolean;
  patch: (b: boolean) => ThemeSettingsPatch;
}
export type ThemeFieldEntry =
  | ThemeCheckboxEntry
  | ThemeColorEntry
  | ThemeGradientEntry
  | ThemeImageEntry
  | ThemeRangeEntry
  | ThemeSelectEntry
  | ThemeTextEntry;

export interface ThemeGroup {
  title: string;
  entries: ThemeFieldEntry[];
}

// ── helpers ─────────────────────────────────────────────────────────────────

const color = (
  id: string,
  label: string,
  info: string,
  get: (t: ThemeSettings) => string,
  patch: (hex: string) => ThemeSettingsPatch,
  contrastAgainstAccent = false,
): ThemeColorEntry => ({
  kind: 'color',
  field: { id, label, info },
  get,
  patch,
  contrastAgainstAccent,
});

const gradient = (
  id: string,
  label: string,
  info: string,
  get: (t: ThemeSettings) => string,
  patch: (css: string) => ThemeSettingsPatch,
): ThemeGradientEntry => ({
  kind: 'gradient',
  field: { id, type: 'gradient', label, info },
  get,
  patch,
});

/**
 * An image the THEME owns, stored as a bare delivery URL (the model has no use for the alt text, the
 * width or the height an `ImageValue` also carries). `noAlt` turns off the control's alt-text input:
 * the one image here is the favicon, and a browser tab shows an icon, never a description.
 */
const image = (
  id: string,
  label: string,
  info: string,
  get: (t: ThemeSettings) => string,
  patch: (url: string) => ThemeSettingsPatch,
): ThemeImageEntry => ({
  kind: 'image',
  field: { id, type: 'image', label, info, noAlt: true },
  get: (t) => {
    const url = get(t);
    return url ? { url, alt: '' } : undefined;
  },
  patch: (v) => patch(v?.url ?? ''),
});

const range = (
  id: string,
  label: string,
  opts: {
    min: number;
    max: number;
    step: number;
    unit?: string;
    info?: string;
    bounds?: 'free' | 'strict';
  },
  get: (t: ThemeSettings) => number,
  patch: (n: number) => ThemeSettingsPatch,
): ThemeRangeEntry => ({
  kind: 'range',
  field: {
    id,
    type: 'range',
    label,
    min: opts.min,
    max: opts.max,
    step: opts.step,
    unit: opts.unit,
    info: opts.info,
    ...(opts.bounds !== undefined ? { bounds: opts.bounds } : {}),
  },
  get,
  patch,
});

const fontSelect = (
  id: string,
  label: string,
  get: (t: ThemeSettings) => string,
  patch: (s: string, t: ThemeSettings) => ThemeSettingsPatch,
): ThemeSelectEntry => ({
  kind: 'select',
  field: { id, type: 'select', label, options: FONT_OPTIONS },
  get,
  patch,
  optionKeyPrefix: 'chrome.theme.font',
});

/** A plain option list (as opposed to {@link fontSelect}, which always offers the curated font stacks). */
const select = (
  id: string,
  label: string,
  options: Array<{ value: string; label: string }>,
  info: string,
  get: (t: ThemeSettings) => string,
  patch: (s: string) => ThemeSettingsPatch,
): ThemeSelectEntry => ({
  kind: 'select',
  field: { id, type: 'select', label, info, options },
  get,
  patch,
  optionKeyPrefix: `${id}.option`,
});

const text = (
  id: string,
  label: string,
  opts: { info?: string; placeholder?: string; maxLength?: number },
  get: (t: ThemeSettings) => string,
  patch: (s: string) => ThemeSettingsPatch,
): ThemeTextEntry => ({
  kind: 'text',
  field: {
    id,
    type: 'text',
    label,
    info: opts.info,
    placeholder: opts.placeholder,
    maxLength: opts.maxLength,
  },
  get,
  patch,
});

const checkbox = (
  id: string,
  label: string,
  info: string,
  get: (t: ThemeSettings) => boolean,
  patch: (b: boolean) => ThemeSettingsPatch,
): ThemeCheckboxEntry => ({
  kind: 'checkbox',
  field: { id, type: 'checkbox', label, info },
  get,
  patch,
});

/** Read one urgency-badge setting, falling back to the shared default for an absent key. */
const badge =
  <K extends keyof typeof DEFAULT_PRODUCT_BADGES>(
    key: K,
  ): ((t: ThemeSettings) => (typeof DEFAULT_PRODUCT_BADGES)[K]) =>
  (t) =>
    (t.productBadges?.[key] ?? DEFAULT_PRODUCT_BADGES[key]) as (typeof DEFAULT_PRODUCT_BADGES)[K];

/**
 * LES DEUX POLICES BOUGENT ENSEMBLE TANT QU'ELLES SONT IDENTIQUES.
 *
 * Le thème porte deux familles (titres / corps) et le catalogue les répartit sur ~70 composants d'une
 * façon que l'œil ne peut pas deviner : le LIBELLÉ d'une ligne à icône (`icon-with-text`,
 * `icons-with-content`, `trust-row`), le texte du logo, les intitulés de colonne du pied de page et le
 * titre d'une rangée dépliable lisent la police des TITRES, alors que rien à l'écran ne les désigne comme
 * des titres. Mesuré le 14/09/2026 : une boutique partie du défaut Poppins/Poppins qui passe la seule
 * « Body font » à Nunito garde **156 textes en Poppins**, éparpillés — la page a l'air cassée, pas
 * re-typographiée. C'est le défaut que la fondatrice a rapporté, capture à l'appui.
 *
 * Les deux familles partant TOUJOURS identiques (le seed et chaque preset nomment une seule famille),
 * « identiques » est l'état normal, et le seul dans lequel un vendeur croit n'avoir qu'une police. On les
 * garde donc liées tant qu'elles le sont : changer l'une change l'autre. Dès qu'elles diffèrent — un
 * preset à deux familles (Hearth, Bloom), ou un vendeur qui les a délibérément séparées via l'autre
 * sélecteur — le lien est rompu et chaque champ redevient indépendant. Aucun libellé nouveau : les deux
 * sélecteurs bougent sous les yeux, ce qui explique la règle mieux qu'une phrase.
 */
const linkedFontPatch =
  (role: 'heading' | 'body') =>
  (v: string, t: ThemeSettings): ThemeSettingsPatch => {
    const linked = t.typography.heading.font === t.typography.body.font;
    if (linked) return { typography: { heading: { font: v }, body: { font: v } } };
    return { typography: { [role]: { font: v } } } as ThemeSettingsPatch;
  };

/** The shared numeric-range specs for the typography sliders (bounds match the backend `normalized()`). */
const SCALE = { min: 0.5, max: 2, step: 0.05, unit: '×' as const };
// Both are RATIOS with no unit, so `rangeBounds.ts` cannot tell from the unit that the ends of these
// tracks are only where the handle stops — marked by hand, for the same reason the block-level line
// height is (founder, 20/09/2026: no text setting is allowed to be a ceiling).
const LINE_HEIGHT = { min: 1, max: 2.5, step: 0.05, bounds: 'free' as const };
const LETTER_SPACING = { min: -2, max: 5, step: 0.1, bounds: 'free' as const };
const WEIGHT = { min: 100, max: 900, step: 100 };

// ── the schema ───────────────────────────────────────────────────────────────

export const THEME_SCHEMA: ThemeGroup[] = [
  {
    // FIRST on purpose. This group exists because the founder asked where the tab icon was uploaded and
    // there was no answer (2026-09-16): nothing in the builder, nothing in the dashboard, and the
    // storefront emitted no `<link rel="icon">` at all. It is one short row, and a seller looking for it
    // finds it without scrolling past the whole palette.
    title: 'Brand',
    entries: [
      image(
        'theme.brand.favicon',
        'Favicon',
        'The small icon browsers show in the tab, in bookmarks and in history. A square image reads best — it is displayed around 16 px wide.',
        (t) => t.brand?.favicon ?? '',
        (v) => ({ brand: { favicon: v } }),
      ),
    ],
  },
  {
    title: 'Color palette',
    entries: [
      color(
        'theme.colors.accent1',
        'Accent 1',
        'Primary brand color — CTAs, links, price, active variant.',
        (t) => t.colors.accent1,
        (v) => ({ colors: { accent1: v } }),
      ),
      color(
        'theme.colors.accent2',
        'Accent 2',
        'Secondary accent (aliases Accent 1 unless a distinct scheme uses it).',
        (t) => t.colors.accent2,
        (v) => ({ colors: { accent2: v } }),
      ),
      gradient(
        'theme.colors.accent2Gradient',
        'Accent gradient',
        'Used where an accent gradient is shown (leave empty for a flat accent).',
        (t) => t.colors.accent2Gradient ?? '',
        (v) => ({ colors: { accent2Gradient: v } }),
      ),
      color(
        'theme.colors.solidButtonLabel',
        'Solid button label',
        'Text/icon color on accent buttons and accent bands.',
        (t) => t.colors.solidButtonLabel,
        (v) => ({ colors: { solidButtonLabel: v } }),
        true,
      ),
      color(
        'theme.colors.outlineButton',
        'Outline button',
        'Outline buttons (also text links).',
        (t) => t.colors.outlineButton,
        (v) => ({ colors: { outlineButton: v } }),
      ),
      color(
        'theme.colors.text',
        'Text',
        'Body + heading text color.',
        (t) => t.colors.text,
        (v) => ({ colors: { text: v } }),
      ),
      color(
        'theme.colors.background1',
        'Background 1',
        'Page background.',
        (t) => t.colors.background1,
        (v) => ({ colors: { background1: v } }),
      ),
      gradient(
        'theme.colors.background1Gradient',
        'Background 1 gradient',
        'Optional page-background gradient (leave empty for a flat color).',
        (t) => t.colors.background1Gradient ?? '',
        (v) => ({ colors: { background1Gradient: v } }),
      ),
      color(
        'theme.colors.background2',
        'Background 2',
        'Cards, surfaces, and alternate sections.',
        (t) => t.colors.background2,
        (v) => ({ colors: { background2: v } }),
      ),
      gradient(
        'theme.colors.background2Gradient',
        'Background 2 gradient',
        'Optional surface gradient (leave empty for a flat color).',
        (t) => t.colors.background2Gradient ?? '',
        (v) => ({ colors: { background2Gradient: v } }),
      ),
    ],
  },
  {
    title: 'Typography',
    entries: [
      fontSelect(
        'theme.typo.heading.font',
        'Heading font',
        (t) => t.typography.heading.font,
        linkedFontPatch('heading'),
      ),
      range(
        'theme.typo.heading.scale',
        'Heading size',
        { ...SCALE, info: 'Scales every heading size in lockstep (1× = current).' },
        (t) => t.typography.heading.scale,
        (v) => ({ typography: { heading: { scale: v } } }),
      ),
      range(
        'theme.typo.heading.lineHeight',
        'Heading line height',
        LINE_HEIGHT,
        (t) => t.typography.heading.lineHeight,
        (v) => ({ typography: { heading: { lineHeight: v } } }),
      ),
      range(
        'theme.typo.heading.letterSpacing',
        'Heading letter spacing',
        LETTER_SPACING,
        (t) => t.typography.heading.letterSpacing,
        (v) => ({ typography: { heading: { letterSpacing: v } } }),
      ),
      range(
        'theme.typo.heading.weight',
        'Heading weight',
        WEIGHT,
        (t) => t.typography.heading.weight,
        (v) => ({ typography: { heading: { weight: v } } }),
      ),
      fontSelect(
        'theme.typo.body.font',
        'Body font',
        (t) => t.typography.body.font,
        linkedFontPatch('body'),
      ),
      range(
        'theme.typo.body.scale',
        'Body size',
        { ...SCALE, info: 'Scales body/label sizes in lockstep (1× = current).' },
        (t) => t.typography.body.scale,
        (v) => ({ typography: { body: { scale: v } } }),
      ),
      range(
        'theme.typo.body.lineHeight',
        'Body line height',
        LINE_HEIGHT,
        (t) => t.typography.body.lineHeight,
        (v) => ({ typography: { body: { lineHeight: v } } }),
      ),
      range(
        'theme.typo.body.letterSpacing',
        'Body letter spacing',
        LETTER_SPACING,
        (t) => t.typography.body.letterSpacing,
        (v) => ({ typography: { body: { letterSpacing: v } } }),
      ),
      range(
        'theme.typo.body.weight',
        'Body weight',
        WEIGHT,
        (t) => t.typography.body.weight,
        (v) => ({ typography: { body: { weight: v } } }),
      ),
    ],
  },
  {
    title: 'Layout',
    entries: [
      range(
        'theme.shape.containerWidth',
        'Container width',
        {
          min: 1000,
          max: 2000,
          step: 20,
          unit: 'px',
          info: 'Max content width for the widest sections — Wide-tier sections plus featured-collection, custom-columns, and slideshow (non-full-width). Narrow/Standard/Full sections are unaffected.',
        },
        (t) => t.shape.containerWidth,
        (v) => ({ shape: { containerWidth: v } }),
      ),
      // Corner radius of EVERY image/video on the storefront (0 = square, the default — founder ask
      // 2026-09-11). Emitted as `--aqx-theme-radius-media`; a section's own "Image corners" setting overrides.
      range(
        'theme.shape.mediaRadius',
        'Image corner radius',
        {
          min: 0,
          max: 40,
          step: 2,
          unit: 'px',
          info: 'Rounds the corners of every image and video on the store (0 = square). Any section can override it with its own "Image corners" setting.',
        },
        (t) => (typeof t.shape.mediaRadius === 'number' ? t.shape.mediaRadius : 0),
        (v) => ({ shape: { mediaRadius: v } }),
      ),
    ],
  },
  {
    /**
     * The URGENCY BADGE, set ONCE for the whole shop (19/09/2026).
     *
     * WHICH products wear one is a per-product decision — the seller picks it in the product sheet, or
     * lets the server deduce it from her stock. What the chip LOOKS like is this group, and it is global
     * on purpose: a chip that changed colour and corner between a collection page and the home page would
     * read as a bug rather than as urgency.
     *
     * Both text fields are optional. Left EMPTY, the storefront writes its own translated label, so an
     * Arabic or Spanish shop reads correctly with nothing filled in. Filled, her words are shown to every
     * shopper in every language — which is the trade free text makes, stated in the field's own hint.
     */
    title: 'Product badges',
    entries: [
      checkbox(
        'theme.productBadges.enabled',
        'Show urgency badges',
        'Pins a small "Sold out" or "High demand" chip on product cards across the store — collection pages, the catalog, featured collections and related products. Off until you turn it on.',
        badge('enabled'),
        (v) => ({ productBadges: { enabled: v } }),
      ),
      select(
        'theme.productBadges.position',
        'Badge position',
        [
          { value: 'top left', label: 'Top left' },
          { value: 'top right', label: 'Top right' },
          { value: 'bottom left', label: 'Bottom left' },
          { value: 'bottom right', label: 'Bottom right' },
        ],
        'Which corner of the product photo the chip sits in. On a right-to-left store the left/right sides follow the reader.',
        badge('position'),
        (v) => ({ productBadges: { position: v as ProductBadgePosition } }),
      ),
      range(
        'theme.productBadges.radius',
        'Badge corner radius',
        { min: 0, max: 40, step: 2, unit: 'px', info: 'Rounds the chip’s corners (0 = square).' },
        badge('radius'),
        (v) => ({ productBadges: { radius: v } }),
      ),
      text(
        'theme.productBadges.highDemandText',
        'High-demand text',
        {
          placeholder: 'High demand',
          maxLength: 40,
          info: 'Leave EMPTY to use our translated wording, which follows the language your store is read in. Anything you type here is shown to every shopper, in every language.',
        },
        badge('highDemandText'),
        (v) => ({ productBadges: { highDemandText: v } }),
      ),
      color(
        'theme.productBadges.highDemandBg',
        'High-demand background',
        'Background colour of the high-demand chip.',
        badge('highDemandBg'),
        (hex) => ({ productBadges: { highDemandBg: hex } }),
      ),
      color(
        'theme.productBadges.highDemandFg',
        'High-demand text colour',
        'Colour of the words inside the high-demand chip.',
        badge('highDemandFg'),
        (hex) => ({ productBadges: { highDemandFg: hex } }),
      ),
      text(
        'theme.productBadges.soldOutText',
        'Sold-out text',
        {
          placeholder: 'Sold out',
          maxLength: 40,
          info: 'Leave EMPTY to use our translated wording. Anything you type here is shown to every shopper, in every language.',
        },
        badge('soldOutText'),
        (v) => ({ productBadges: { soldOutText: v } }),
      ),
      color(
        'theme.productBadges.soldOutBg',
        'Sold-out background',
        'Background colour of the sold-out chip.',
        badge('soldOutBg'),
        (hex) => ({ productBadges: { soldOutBg: hex } }),
      ),
      color(
        'theme.productBadges.soldOutFg',
        'Sold-out text colour',
        'Colour of the words inside the sold-out chip.',
        badge('soldOutFg'),
        (hex) => ({ productBadges: { soldOutFg: hex } }),
      ),
    ],
  },
];
